import React from 'react';
import { StageSection, Prose, KeyPoints } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Programs: Stateless Executable Accounts',
    tagline: 'Code that runs against whatever data accounts you hand it.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - why a single program instance can manage countless data accounts (e.g. the Token Program)
//   Q1 - the structural difference between a Solana program and an Ethereum contract
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 3 of 8" title="A program doesn't hold its own state">
            <Prose>
                A Solana program is itself just an account, one with its <code>executable</code> flag set to true.
                But unlike an Ethereum contract, it doesn't carry its own mutable storage. A program's logic operates
                entirely on separate accounts that are explicitly passed into it by whoever calls it. Two calls to
                the exact same program can operate on two completely different sets of data, because the data was
                never part of the program in the first place.
            </Prose>
        </StageSection>
    )),

    content('one-program-many-accounts', () => (
        <StageSection title="One program, unlimited data accounts">
            <Prose>
                This is why a single deployed program (like the SPL Token Program, which underlies essentially
                every fungible token on Solana) can manage millions of different tokens without being redeployed
                for each one. Every token has its own separate mint account and separate holder accounts; the same
                Token Program code just gets invoked against whichever ones a given transaction supplies. Contrast
                this with Ethereum, where every ERC-20 token is typically its own separately deployed contract, each
                with code and storage bundled together.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('implications', () => (
        <StageSection title="What the split makes possible">
            <Prose>
                Because a program's code lives independently of the data it operates on, a program's executable
                account can potentially be upgraded (replacing the code) without touching any of the countless data
                accounts it operates on, a very different upgrade story than an immutable Ethereum contract, with
                its own trade-offs that the deeper Solana Advanced and security tracks explore.
            </Prose>
            <KeyPoints items={[
                <>Programs are stateless code; accounts are where state actually lives.</>,
                <>Next: how a transaction tells a program exactly which accounts to operate on.</>,
            ]} />
        </StageSection>
    )),

    question(1),
];
